import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Tema por curso (año) del alumno. El curso (1..6) define la paleta de la UI;
 * el servicio lo aplica seteando `data-course="N"` en <html>, lo que activa el
 * bloque de CSS custom properties correspondiente (ver
 * `src/styles/theme/_course-themes.scss`). Las pantallas usan `var(--gr-…)`,
 * así que cambian de tema sin lógica propia.
 *
 * El curso llega en el login (cuando exista el campo en el backend) y se
 * persiste —igual patrón que `gr_user_type`— para sobrevivir reloads.
 */
@Injectable({ providedIn: 'root' })
export class CourseThemeService {
  /** Clave de persistencia (sobrevive reloads; se limpia en el logout). */
  private static readonly STORAGE_KEY = 'gr_course';
  /** Rango válido de cursos y default/fallback (curso 1). */
  private static readonly MIN = 1;
  private static readonly MAX = 6;
  private static readonly DEFAULT = 1;
  /** Atributo que dispara el tema en el CSS global. */
  private static readonly ATTR = 'data-course';

  private readonly course$ = new BehaviorSubject<number>(CourseThemeService.DEFAULT);

  /** Curso activo (1..6) como observable, por si una pantalla necesita el dato
   *  numérico además del tema CSS. */
  readonly currentCourse$ = this.course$.asObservable();

  get currentCourse(): number {
    return this.course$.getValue();
  }

  /** Aplica el tema de un curso: setea `data-course` en <html> y lo persiste.
   *  Acepta number o string (lo que mande el backend); normaliza al rango. */
  setCourse(course: number | string | null | undefined): void {
    const n = this.normalize(course);
    document.documentElement.setAttribute(CourseThemeService.ATTR, String(n));
    localStorage.setItem(CourseThemeService.STORAGE_KEY, String(n));
    this.course$.next(n);
  }

  /** Reaplica el curso persistido al arrancar la app (o el default si no hay).
   *  Llamar una vez desde `AppComponent`. */
  restore(): void {
    this.setCourse(localStorage.getItem(CourseThemeService.STORAGE_KEY));
  }

  /**
   * SVG de la insignia de un capítulo (colección estática por curso). La clave
   * es el ORDEN 1-based del capítulo — única fuente de verdad del path, usada
   * por la colección del perfil y por la celebración del cuestionario para que
   * ambas pantallas muestren siempre la misma insignia.
   */
  badgeImage(chapterOrder: number): string {
    return `assets/v3/profile/badges/course-${this.currentCourse}/chapter-${chapterOrder}.svg`;
  }

  /** Limpia el tema en el logout: borra lo persistido y vuelve al default. */
  clear(): void {
    localStorage.removeItem(CourseThemeService.STORAGE_KEY);
    document.documentElement.setAttribute(
      CourseThemeService.ATTR,
      String(CourseThemeService.DEFAULT),
    );
    this.course$.next(CourseThemeService.DEFAULT);
  }

  /** Lleva cualquier entrada a un curso válido (fuera de rango o no numérico →
   *  default), así un dato inesperado del backend nunca rompe el tema. */
  private normalize(course: number | string | null | undefined): number {
    const n = Math.trunc(Number(course));
    if (!Number.isFinite(n) || n < CourseThemeService.MIN || n > CourseThemeService.MAX) {
      return CourseThemeService.DEFAULT;
    }
    return n;
  }
}
